import { useTranslation } from "react-i18next";
import Checkbox from "@mui/material/Checkbox";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Chip from "@mui/material/Chip";
import { GetProjectResponse, WorkItemResponse } from "api/generatedApi";
import { costumPalette } from "theme/palette";
import { ExtendedProjectFolder } from "pages/project/hooks/use-map-tree-to-flat";
import { getHmsFromMilliSeconds, formatNumberToPrice, formatNumberToDkNumber } from "utils/formats";
import { useWorkItemRestrictions } from "shared/user-restrictions/use-workitems-restrictions";
import { useDialog } from "shared/dialog/use-dialog";
import { ShowWorkItemInfoDialog } from "../../info/show-work-item-info-dialog";
import { useEditWorkItemAmount } from "../../edit/edit-work-item-amount/use-edit-work-item-amount";
import { ItemDot } from "components/shared/item-dot/item-dot";
import { useWorkitem } from "../../hooks/use-workitem";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";

interface Prop {
  project: GetProjectResponse;
  folder: ExtendedProjectFolder;
  workItem: WorkItemResponse;
  onCheck: (id: string | undefined) => void;
  checked: boolean;
  shouldScrollIntoView?: boolean;
  setLastEdited: Dispatch<SetStateAction<string | null>>;
  scrollToRow: (rowRef: React.RefObject<HTMLTableRowElement>, shouldScrollIntoView: boolean) => void;
  sortItemsState: boolean;
  changeWorkItemsOrderindex: (workItemId: string, direction: "up" | "down") => void;
}

export const DesktopWorkItemOperationTableRow = (props: Prop) => {
  const { project, folder, workItem, onCheck, checked, shouldScrollIntoView, sortItemsState, changeWorkItemsOrderindex } = props;
  const { t } = useTranslation();
  const { canSelectWorkItem, canEditWorkitem } = useWorkItemRestrictions(project);
  const [openInfoDialog] = useDialog(ShowWorkItemInfoDialog);
  const openEditAmountDialog = useEditWorkItemAmount({ setLastEdited: props.setLastEdited, project, folder, workItem });
  const { workItemIsRecognised, workItemTitle } = useWorkitem({ workItem });

  const showWorkItemInfo = () => {
    openInfoDialog({ project, folder, workItem, setLastEdited: props.setLastEdited });
    props.setLastEdited(workItem.workItemId ?? null);
  };

  const rowRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    props.scrollToRow(rowRef, shouldScrollIntoView ?? false);
  }, [shouldScrollIntoView, props.scrollToRow]);

  return (
    <TableRow ref={rowRef} data-testid={`measurements-operation-table-row-${workItem.workItemText}`} sx={{ cursor: "pointer" }}>
      {!sortItemsState && (
        <TableCell>
          <Checkbox data-testid="measurement-table-row-operation-checkbox" onChange={(event) => onCheck(workItem.workItemId)} onClick={(event) => event.stopPropagation()} checked={checked} disabled={!canSelectWorkItem(folder)} />
        </TableCell>
      )}

      {sortItemsState && (
        <TableCell sx={{ color: "primary.main" }} data-testid="measurements-table-cell-orderIndex" width={"fit-content"}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "fit-content" }}>
            <IconButton size="small" onClick={() => changeWorkItemsOrderindex?.(workItem.workItemId ?? "", "up")} sx={{ pb: 0 }}>
              <ExpandLessIcon />
            </IconButton>
            <IconButton size="small" onClick={() => changeWorkItemsOrderindex?.(workItem.workItemId ?? "", "down")} sx={{ pt: 0 }}>
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        </TableCell>
      )}
      <TableCell sx={{ color: "primary.main" }} data-testid="measurements-table-cell-operation" title={workItemTitle} onClick={!sortItemsState ? showWorkItemInfo : undefined}>
        {!workItemIsRecognised && <ItemDot sx={{ marginRight: "5px" }} />}
        {workItem.workItemText}
      </TableCell>
      <TableCell sx={{ color: "primary.main" }} data-testid="measurements-table-cell-moutingcode" onClick={!sortItemsState ? showWorkItemInfo : undefined}></TableCell>
      <TableCell sx={{ color: "primary.main" }} data-testid="measurements-table-cell-supplements" onClick={!sortItemsState ? showWorkItemInfo : undefined}>
        {workItem.supplements && workItem.supplements.length > 0 && (
          <Chip data-testid="measurements-table-cell-supplements-chip-supplement" sx={{ mb: "5px", bgcolor: costumPalette.supplementBg, color: costumPalette.supplementColor }} label={t("content.measurements.table.cellValue.supplement")} />
        )}
      </TableCell>
      <TableCell sx={{ color: "primary.main", textAlign: "right", pr: 5 }} data-testid="measurements-table-cell-amount" onClick={!sortItemsState ? (canEditWorkitem(folder) ? openEditAmountDialog : showWorkItemInfo) : undefined}>
        {formatNumberToDkNumber(workItem.workItemAmount)}
      </TableCell>
      <TableCell sx={{ color: "primary.main", textAlign: "right", pr: 5 }} data-testid="measurements-table-cell-total-operationtime" onClick={!sortItemsState ? showWorkItemInfo : undefined}>
        {getHmsFromMilliSeconds(workItem.workItemTotalOperationTimeMilliseconds)}
      </TableCell>
      <TableCell sx={{ color: "primary.main", textAlign: "right", pr: 5 }} data-testid="measurements-table-cell-total-payment" onClick={!sortItemsState ? showWorkItemInfo : undefined}>
        {formatNumberToPrice(workItem.workItemTotalPaymentDkr)}
      </TableCell>
    </TableRow>
  );
};
